import apiClient from './apiClient';
import { GraphData } from '@/types/graph';

const apiBaseURL = '/api/kb';

/** A subfolder holding documents, e.g. `/contracts/2024/`, with its direct document count. */
export interface KBFolderInfo {
  path: string;
  document_count: number;
}

export interface CadrageInfo {
  id: string;
  name: string;
  document_count: number;
  folders?: KBFolderInfo[];
}

export interface UseCaseInfo {
  id: string;
  name: string;
  document_count: number;
  folders?: KBFolderInfo[];
  cadrages: CadrageInfo[];
}

/** A project the user may open, with the files of its current version sent to the knowledge base. */
export interface ProjectKBInfo {
  id: string;
  name: string;
  document_count: number;
  indexed_count: number;
  /** Unpublished: visible to its author, admins and reviewers only. */
  is_private: boolean;
}

export interface DomainInfo {
  id: string;
  name: string;
  description?: string;
  document_count: number;
  folders?: KBFolderInfo[];
  use_cases: UseCaseInfo[];
  projects?: ProjectKBInfo[];
}

export interface CustomFolderInfo {
  id: string;
  name: string;
  description?: string;
  minio_path: string;
  document_count: number;
}

export interface KBStructure {
  business: DomainInfo[];
  custom: CustomFolderInfo[];
}

export interface KBStats {
  total_documents: number;
  indexed_documents: number;
  pending_documents: number;
  total_contexts: number;
}

export interface KnowledgeImportResult {
  document_id: string;
  entities_imported: number;
  relationships_imported: number;
  warnings: string[];
}

export interface GraphPassage {
  chunk_id?: string | null;
  text: string;
  chunk_index?: number | null;
}

export interface GraphSourceDocument {
  document_id: string;
  document_postgres_id?: number | null;
  filename: string;
  passages: GraphPassage[];
}

export interface GraphSources {
  documents: GraphSourceDocument[];
}

export const kbService = {
  /**
   * Get complete KB structure (business + custom folders) in one call
   */
  async getStructure(): Promise<KBStructure> {
    const response = await apiClient.get<KBStructure>(`${apiBaseURL}/structure`);
    return response.data;
  },

  /**
   * Get KB statistics
   */
  async getStats(): Promise<KBStats> {
    const response = await apiClient.get<KBStats>(`${apiBaseURL}/stats`);
    return response.data;
  },

  /**
   * Get context graph data for visualization, optionally scoped to one domain
   */
  async getContextGraph(domainId?: string): Promise<GraphData> {
    const response = await apiClient.get<GraphData>(`${apiBaseURL}/context-graph`, {
      params: domainId ? { domain_id: domainId } : undefined,
    });
    return response.data;
  },

  /**
   * Source documents and passages for a focused concept (`node`) or a pinned link.
   */
  async getSources(params: {
    node?: string;
    source?: string;
    target?: string;
  }): Promise<GraphSources> {
    const response = await apiClient.get<GraphSources>(`${apiBaseURL}/sources`, {
      params: {
        node_id: params.node,
        source_id: params.source,
        target_id: params.target,
      },
    });
    return response.data;
  },

  /**
   * Import knowledge from an ontology/graph file (.json, .jsonld, .ttl, .owl, .rdf, .xml).
   * Org-admin only on the backend side.
   */
  async importKnowledge(
    file: File,
    options?: { domainId?: string; themeCode?: string }
  ): Promise<KnowledgeImportResult> {
    const formData = new FormData();
    formData.append('file', file);
    if (options?.domainId) formData.append('domain_id', options.domainId);
    if (options?.themeCode) formData.append('theme_code', options.themeCode);

    const response = await apiClient.post<KnowledgeImportResult>(
      `${apiBaseURL}/import`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },
};

export default kbService;
